import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
  bulkCreatePipingProjectDocuments,
  bulkCreateProjectDocuments,
  createProjectDocument,
  deleteProjectDocument,
  deleteProjectDocumentWithReason,
  getAllProjectDocumentNos,
  getProjectDocument,
  getProjectDocuments,
  updateProjectDocument
} from '../../api/project-documents';
import { AccessControl } from '../MasterData/AccessControl/accessControlSlice';
import { Contractor } from '../MasterData/Contractor/contractorSlice';
import { Discipline } from '../MasterData/Discipline/disciplineSlice';
import { DocumentParam } from '../Project/ProjectView';
import { getProjectAsync } from '../Project/projectSlice';

export const getProjectDocumentsAsync = createAsyncThunk(
  'projectDocuments/getProjectDocuments',
  getProjectDocuments
);

export const getProjectDocumentAsync = createAsyncThunk(
  'projectDocuments/getProjectDocument',
  async (projectDocumentId: number, thunkAPI) => {
    const projectDocument = await getProjectDocument(projectDocumentId);
    thunkAPI.dispatch(getProjectAsync(projectDocument.projectId));
    return projectDocument;
  }
);

export const getAllProjectDocumentNosAsync = createAsyncThunk(
  'projectDocuments/getAllProjectDocumentNos',
  getAllProjectDocumentNos
);

export const createProjectDocumentAsync = createAsyncThunk(
  'projectDocuments/createProjectDocument',
  createProjectDocument
);

export const bulkCreateProjectDocumentsAsync = createAsyncThunk(
  'projectDocuments/bulkCreateProjectDocuments',
  bulkCreateProjectDocuments
);

export const bulkCreatePipingProjectDocumentsAsync = createAsyncThunk(
  'projectDocuments/bulkCreatePipingProjectDocuments',
  bulkCreatePipingProjectDocuments
);

export const updateProjectDocumentAsync = createAsyncThunk(
  'projectDocuments/updateProjectDocument',
  updateProjectDocument
);

export const deleteProjectDocumentWithReasonAsync = createAsyncThunk(
  'projectDocuments/deleteProjectDocumentWithReason',
  async (
    {
      id,
      reason,
      documentParams
    }: {
      id: number;
      reason: string;
      documentParams: DocumentParam;
    },
    thunkAPI
  ) => {
    await deleteProjectDocumentWithReason({ id, reason });
    thunkAPI.dispatch(getProjectDocumentsAsync(documentParams));
  }
);

export const deleteProjectDocumentAsync = createAsyncThunk(
  'projectDocuments/deleteProjectDocument',
  async (
    {
      projectDocumentId,
      documentParams
    }: { projectDocumentId: number; documentParams: DocumentParam },
    thunkAPI
  ) => {
    await deleteProjectDocument(projectDocumentId);
    thunkAPI.dispatch(getProjectDocumentsAsync(documentParams));
  }
);

export interface ProjectDocument {
  id: number;
  name: string;
  no: string;
  consultantId: number | null;
  consultant: Contractor | null;
  contractorId: number | null;
  contractor: Contractor | null;
  subContractorId: number | null;
  subContractor: Contractor | null;
  clientDocumentNo: string | null;
  epcDocumentNo: string | null;
  vendorDocumentNo: string | null;
  documentCreatorId: number;
  documentId: number;
  disciplineId: number;
  discipline: Discipline;
  projectId: number;
  isAIMS: boolean;
  aimsDocumentNo: string | null;
  accessControlId: number;
  accessControl: AccessControl;
  projectDocumentCustomAccessControls: { id: number; userId: number }[];
}

interface ProjectDocumentState {
  data: ProjectDocument[];
  count: number;
  selectedProjectDocument: ProjectDocument | null;
  createdProjectDocument: ProjectDocument | null;
  allProjectDocumentNos: string[];
}

const initialState: ProjectDocumentState = {
  data: [],
  count: 0,
  selectedProjectDocument: null,
  createdProjectDocument: null,
  allProjectDocumentNos: []
};

export default createSlice({
  name: 'projectDocuments',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(getProjectDocumentsAsync.fulfilled, (state, action) => {
      state.data = action.payload.data;
      state.count = action.payload.count;
    });
    builder.addCase(getProjectDocumentAsync.fulfilled, (state, action) => {
      state.selectedProjectDocument = action.payload;
    });
    builder.addCase(createProjectDocumentAsync.fulfilled, (state, action) => {
      state.createdProjectDocument = action.payload;
    });
    builder.addCase(
      getAllProjectDocumentNosAsync.fulfilled,
      (state, action) => {
        state.allProjectDocumentNos = action.payload.data;
      }
    );
  }
});
